import Phaser from 'phaser';
import { Enemy } from './Enemy';
import type { BossConfig } from './BossConfig';
import type { ChargingBossBehavior, ChargingPhase } from './boss-behaviors/ChargingBossBehavior';

// Idle/standing-кадр в run-листе для каждого босса. Дефолт 0 — естественная
// поза стоя (ноги вытянуты). Override завести при несовпадении: ключ = boss id,
// значение = индекс кадра в его run-spritesheet. Big_boar использует дефолт.
// Camel: 4-й кадр (index 3) — лапы вытянуты, естественная стойка верблюда;
// frame 0 застаёт его в полу-шаге с поджатой передней.
// Bear: 2-й кадр (index 1) — естественная стойка; frame 0 — мид-шаг.
const IDLE_FRAME_BY_ID: Record<string, number> = {
  camel: 3,
  bear: 1,
};

export class Boss extends Enemy {
  public readonly bossConfig: BossConfig;
  // Однократное воскрешение: после первого hp<=0 показываем фейк-победу и встаём
  // с reviveAtHp долей max. hasRevived гарантирует только один раз. isInFakeDeath —
  // между фейк-смертью и performRevive: tick/коллизия/перевыброс death-события заморожены.
  public hasRevived = false;
  public isInFakeDeath = false;
  // Последняя фаза, под которую настраивали спрайт-анимацию. Нужно чтобы не
  // дёргать pause/timeScale/resume каждый кадр — только на переходе.
  private lastSpritePhase?: ChargingPhase;
  // Glow-эффект на спрайте в фазе неуязвимости (лось в charge). Активен пока
  // isInvulnerable=true. Хранится для последующего postFX.remove().
  private invulnGlowFx?: Phaser.FX.Glow;
  // Bob squash/stretch tween (медоед в charge). Базовые scale зафиксированы
  // при старте — на stop() возвращаемся к ним, чтобы спрайт не "застрял"
  // в полу-сжатом состоянии.
  private bobTween?: Phaser.Tweens.Tween;
  private bobBaseScaleX?: number;
  private bobBaseScaleY?: number;

  constructor(scene: Phaser.Scene, x: number, y: number, config: BossConfig) {
    super(scene, x, y, config);
    this.bossConfig = config;
    // Initialize behavior params from chargingParams
    if (config.bossType === 'charging' && config.chargingParams) {
      // Re-init the behavior with charging params — Enemy.init() уже вызвался,
      // но не знал про chargingParams (они не в movementParams).
      this.behavior.init(
        this,
        config.chargingParams as unknown as Record<string, number>,
      );
    }
  }

  override tick(deltaMs: number, target: { x: number; y: number }): void {
    if (this.isInFakeDeath) return;
    super.tick(deltaMs, target);
    // Sync invulnerability from current phase — Enemy.damage() сам проверит isInvulnerable
    // и сыграет белую spark-вспышку через triggerInvulnerableHitFlash (как у Жука).
    this.isInvulnerable = this.isInvulnerableNow();
    this.applyPhaseTint();
    this.applyPhaseSpriteAnim();
    this.applyInvulnerableGlow();
  }

  override damage(amount: number): boolean {
    // Во время fake-death (1.5с между фейковой "ПОБЕДА" и performRevive) пули
    // не должны добивать босса — иначе повторно сработает kill-ветка в GameScene
    // и босс будет splice'нут/destroy'нут до того как воскрешение успеет сработать.
    if (this.isInFakeDeath) return false;
    return super.damage(amount);
  }

  shouldReviveInsteadOfDeath(): boolean {
    if (!this.bossConfig.reviveOnce) return false;
    if (this.hasRevived) return false;
    if (this.isInFakeDeath) return false;
    return true;
  }

  performRevive(): void {
    const r = this.bossConfig.reviveOnce;
    if (!r) return;

    this.hasRevived = true;
    this.isInFakeDeath = false;
    this.currentHp = Math.floor(this.maxHp * r.reviveAtHp);

    // Визуальное уменьшение — collision GameScene должен использовать displayWidth/Height,
    // которые учитывают scale, иначе хитбокс остался бы прежним.
    this.setScale(r.postReviveSizeMul);

    // Пере-init behavior: новый базовый chargeSpeed × postReviveSpeedMul, фаза idle (пауза перед атакой).
    const baseParams = this.bossConfig.chargingParams;
    if (baseParams) {
      this.behavior.init(this, {
        idleMs: baseParams.idleMs,
        windupMs: baseParams.windupMs,
        chargeSpeed: baseParams.chargeSpeed * r.postReviveSpeedMul,
        recoveryMs: baseParams.recoveryMs,
      });
    }

    // Сбросить hp-tint к base — следующий applyPhaseTint поставит phase tint.
    this.combatColor = this.baseColor;
    this.setFillStyle(this.baseColor);
  }

  getChargingPhase(): ChargingPhase | undefined {
    if (this.bossConfig.bossType !== 'charging') return undefined;
    return (this.behavior as ChargingBossBehavior).phase;
  }

  isInvulnerableNow(): boolean {
    const phases = this.bossConfig.invulnerableInPhases;
    if (!phases || phases.length === 0) return false;
    const phase = this.getChargingPhase();
    if (!phase) return false;
    return phases.includes(phase);
  }

  // Белая вспышка при смене hp-фазы. Использует combatColor (protected в Enemy,
  // доступен через наследование) для корректного возврата к текущему phase tint.
  // Для спрайт-боссов Rectangle прозрачен — зеркалим вспышку через sprite.setTint.
  flashPhaseChange(): void {
    this.setFillStyle(0xffffff);
    this.sprite?.setTint(0xffffff);
    this.scene.time.delayedCall(120, () => {
      if (this.getIsDying()) return;
      this.setFillStyle(this.combatColor);
      this.sprite?.setTint(this.combatColor);
    });
  }

  private applyPhaseTint(): void {
    const phaseTints = this.bossConfig.phaseTints;
    if (!phaseTints) return;
    if (this.bossConfig.bossType !== 'charging') return;
    const behavior = this.behavior as ChargingBossBehavior;
    const phase: ChargingPhase = behavior.phase;
    const tint = phaseTints[phase];
    if (tint !== undefined && this.combatColor !== tint) {
      this.combatColor = tint;
      this.setFillStyle(tint);
      // Для спрайт-боссов Rectangle alpha=0 (см. Enemy.constructor), setFillStyle
      // не виден. setTint мультипликативно тонирует PNG — idle ≈ натуральный
      // окрас, windup → оранжевый отблеск, charge → красный, recovery → тёмный.
      this.sprite?.setTint(tint);
    }
  }

  // Idle-кадр в run-листе. Дефолт 0 — натуральная поза стоя (ноги вытянуты).
  // На остановке (idle/recovery) фиксируемся на нём, а не на «текущем» —
  // иначе boss замирает в середине шага с поджатыми ногами.
  private getIdleFrame(): number {
    return IDLE_FRAME_BY_ID[this.bossConfig.id] ?? 0;
  }

  // Управление спрайт-анимацией по фазе. Два режима:
  //
  //  Big_boar-style (default, animateOnlyOnCharge !== true):
  //    idle     — anims.stop() + setFrame(idleFrame) (натуральная стойка)
  //    windup   — play(run, true) + timeScale 0.5 (медленный замах копыт)
  //    charge   — play(run, true) + timeScale 1.5 (бешеный галоп)
  //    recovery — anims.stop() + setFrame(idleFrame) (врезался и стоит, ноги вытянуты)
  //
  //  Elk-style (animateOnlyOnCharge === true):
  //    charge   — play(elk_run, true) на timeScale 1.5 (галоп)
  //    остальные — anims.stop() + setFrame(idleFrame) (стоп-кадр, без бега на месте)
  //
  // Дёргается только на переходе фаз — `lastSpritePhase` блокирует пере-вызов
  // каждый tick. Безопасен для Rectangle-only боссов (без sprite) — ранний return.
  private applyPhaseSpriteAnim(): void {
    if (!this.sprite) return;
    if (this.bossConfig.bossType !== 'charging') return;
    const phase = this.getChargingPhase();
    if (!phase) return;
    if (phase === this.lastSpritePhase) return;
    this.lastSpritePhase = phase;

    const anims = this.sprite.anims;

    // Флип под направление будущего рывка — общий для всех режимов, только
    // на windup. chargeDirX зафиксирован в transitionToWindup ДО смены phase.
    // PNG нарисован мордой ВПРАВО → flipX=true даёт морду влево. В idle/recovery
    // флип не трогаем — сохраняется последнее направление (естественно для
    // оглушённого/отдыхающего босса).
    if (phase === 'windup') {
      const dirX = (this.behavior as ChargingBossBehavior).chargeDirectionX;
      if (Math.abs(dirX) > 0.01) {
        this.sprite.setFlipX(dirX < 0);
      }
    }

    // Bob squash/stretch (медоед). Запускается на charge, останавливается
    // в остальных фазах. Независим от анимационной логики ниже — у медоеда
    // нет spriteRunAnimKey, ранний return пропустит switch.
    if (this.bossConfig.bobOnCharge) {
      if (phase === 'charge') this.startBobTween();
      else this.stopBobTween();
    }

    // Если у босса нет run-анимации (медоед: одна плоская текстура),
    // управлять кадровой анимацией бессмысленно — выходим.
    if (!this.spriteRunAnimKey) return;

    if (this.bossConfig.animateOnlyOnCharge) {
      // Elk-стиль: ноги двигаются ТОЛЬКО в charge (= движение). В покое —
      // idle-кадр (натуральная поза), чтобы не было "бега на месте".
      if (phase === 'charge') {
        anims.timeScale = 1.5;
        if (this.spriteRunAnimKey) {
          // play(key, true) с ignoreIfPlaying=true — рестартует если была
          // остановлена, не трогает если уже играет (защита от reset кадра).
          this.sprite.play(this.spriteRunAnimKey, true);
        }
      } else {
        anims.timeScale = 1;
        anims.stop();
        this.sprite.setFrame(this.getIdleFrame());
      }
      return;
    }

    // Big_boar-стиль: на остановке (idle/recovery) — stop()+idleFrame,
    // в движении (windup/charge) — анимация играет с разным timeScale.
    // play(key, true) с ignoreIfPlaying=true: после stop() стартует с нуля,
    // на windup→charge не рестартует (продолжает с текущего кадра).
    switch (phase) {
      case 'idle':
        anims.timeScale = 1;
        anims.stop();
        this.sprite.setFrame(this.getIdleFrame());
        break;
      case 'windup':
        if (this.spriteRunAnimKey) this.sprite.play(this.spriteRunAnimKey, true);
        anims.timeScale = 0.5;
        break;
      case 'charge':
        if (this.spriteRunAnimKey) this.sprite.play(this.spriteRunAnimKey, true);
        anims.timeScale = 1.5;
        break;
      case 'recovery':
        anims.timeScale = 1;
        anims.stop();
        this.sprite.setFrame(this.getIdleFrame());
        break;
    }
  }

  // Squash/stretch yoyo-tween на спрайте — "качок от земли" вместо лап-цикла
  // (медоед на charge). Origin (0.5, 1) делает анкер внизу: scaleY×0.92
  // сжимает верх вниз, scaleX×1.05 расширяет в стороны от низа. Базовые
  // scale фиксируются на старте — на stop() возвращаемся к ним.
  private startBobTween(): void {
    if (!this.sprite) return;
    if (this.bobTween) return;
    this.bobBaseScaleX = this.sprite.scaleX;
    this.bobBaseScaleY = this.sprite.scaleY;
    this.bobTween = this.scene.tweens.add({
      targets: this.sprite,
      scaleY: this.bobBaseScaleY * 0.92,
      scaleX: this.bobBaseScaleX * 1.05,
      duration: 110,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private stopBobTween(): void {
    if (!this.bobTween) return;
    this.bobTween.stop();
    this.bobTween = undefined;
    if (
      this.sprite &&
      this.bobBaseScaleX !== undefined &&
      this.bobBaseScaleY !== undefined
    ) {
      this.sprite.scaleX = this.bobBaseScaleX;
      this.sprite.scaleY = this.bobBaseScaleY;
    }
  }

  // Свечение поверх спрайта в фазе неуязвимости (лось в charge: визуальный
  // сигнал "пули отскакивают"). Включается на isInvulnerable=true, снимается
  // при выходе. Реализация через Phaser postFX.addGlow — WebGL only, Phaser
  // 3.60+. На Canvas-рендере postFX undefined → silent skip (в проекте WebGL,
  // canvas-fallback не нужен; если когда-нибудь понадобится — setTint
  // конфликтует с phase tint, понадобится отдельная стратегия).
  private applyInvulnerableGlow(): void {
    if (!this.sprite) return;
    // showInvulnerableGlow !== false: default включён (лось). false у медоеда —
    // glow выключен, инвалидность сохраняется через isInvulnerable.
    if (this.bossConfig.showInvulnerableGlow === false) return;
    const wantsGlow = this.isInvulnerable;
    if (wantsGlow && !this.invulnGlowFx) {
      // 0x39FF14 = COLORS.green (PROJECT.md: маркер заражения / "не стрелять").
      // outerStrength=4, innerStrength=0, knockout=false, quality=0.1, distance=12.
      const fx = this.sprite.postFX?.addGlow(0x39ff14, 4, 0, false, 0.1, 12);
      if (fx) this.invulnGlowFx = fx;
    } else if (!wantsGlow && this.invulnGlowFx) {
      this.sprite.postFX?.remove(this.invulnGlowFx);
      this.invulnGlowFx = undefined;
    }
  }
}
